import type { LocalizedDefaultStrings, SupportedLanguage } from './types';

const LOCALIZED_DEFAULTS: Record<SupportedLanguage, LocalizedDefaultStrings> = {
  en: {
    reminderMessage:
      "Don't forget your appointment with {from} on {date} at {time} at {eventlocation}.",
    testReminderMessage: 'Reminder: {title} on {date} at {time}.',
    untitledEventLabel: 'your appointment',
    legacyReminderMessage: 'Reminder: {title} on {date} at {time}.',
    confirmationMessage: 'Confirmed: {title} on {date} at {time}.',
    followUpMessage: 'Thank you from {from}!',
    confirmationLinkSuffix: 'Confirm or cancel: {url}',
    templateName: 'My First Template',
    senderName: 'My Business',
    location: '123 Main Street',
    calendarStatusLabels: {
      pending: 'Pending',
      confirmed: 'Confirmed',
      cancelled: 'Cancelled',
      lateCancelled: 'Late Cancelled',
    },
    tokenAliases: {},
  },
  es: {
    reminderMessage:
      'No olvides tu cita con {remitente} el {fecha} a las {hora} en {ubicacionevento}.',
    testReminderMessage: 'Recordatorio: {titulo} el {fecha} a las {hora}.',
    untitledEventLabel: 'tu cita',
    legacyReminderMessage: 'Recordatorio: {titulo} el {fecha} a las {hora}.',
    confirmationMessage: 'Confirmado: {titulo} el {fecha} a las {hora}.',
    followUpMessage: '¡Gracias de parte de {remitente}!',
    confirmationLinkSuffix: 'Confirma o cancela: {url}',
    templateName: 'Mi Primera Plantilla',
    senderName: 'Mi Negocio',
    location: 'Calle Principal 123',
    calendarStatusLabels: {
      pending: 'Pendiente',
      confirmed: 'Confirmado',
      cancelled: 'Cancelado',
      lateCancelled: 'Cancelado tarde',
    },
    tokenAliases: {
      title: 'titulo',
      date: 'fecha',
      time: 'hora',
      from: 'remitente',
      location: 'ubicacion',
      eventlocation: 'ubicacionevento',
      videolink: 'enlacevideo',
    },
  },
  'pt-BR': {
    reminderMessage:
      'Não esqueça seu compromisso com {remetente} em {data} às {hora} em {localevento}.',
    testReminderMessage: 'Lembrete: {titulo} em {data} às {hora}.',
    untitledEventLabel: 'seu compromisso',
    legacyReminderMessage: 'Lembrete: {titulo} em {data} às {hora}.',
    confirmationMessage: 'Confirmado: {titulo} em {data} às {hora}.',
    followUpMessage: 'Obrigado de {remetente}!',
    confirmationLinkSuffix: 'Confirme ou cancele: {url}',
    templateName: 'Meu Primeiro Modelo',
    senderName: 'Meu Negócio',
    location: 'Rua Principal, 123',
    calendarStatusLabels: {
      pending: 'Pendente',
      confirmed: 'Confirmado',
      cancelled: 'Cancelado',
      lateCancelled: 'Cancelado tarde',
    },
    tokenAliases: {
      title: 'titulo',
      date: 'data',
      time: 'hora',
      from: 'remetente',
      location: 'local',
      eventlocation: 'localevento',
      videolink: 'linkvideo',
    },
  },
  de: {
    reminderMessage:
      'Vergiss nicht deinen Termin mit {von} am {datum} um {uhrzeit} Uhr bei {veranstaltungsort}.',
    testReminderMessage: 'Erinnerung: {titel} am {datum} um {uhrzeit} Uhr.',
    untitledEventLabel: 'dein Termin',
    legacyReminderMessage: 'Erinnerung: {titel} am {datum} um {uhrzeit} Uhr.',
    confirmationMessage: 'Bestätigt: {titel} am {datum} um {uhrzeit} Uhr.',
    followUpMessage: 'Vielen Dank von {von}!',
    confirmationLinkSuffix: 'Bestätigen oder absagen: {url}',
    templateName: 'Meine Erste Vorlage',
    senderName: 'Mein Unternehmen',
    location: 'Hauptstraße 123',
    calendarStatusLabels: {
      pending: 'Ausstehend',
      confirmed: 'Bestätigt',
      cancelled: 'Storniert',
      lateCancelled: 'Spät storniert',
    },
    tokenAliases: {
      title: 'titel',
      date: 'datum',
      time: 'uhrzeit',
      from: 'von',
      location: 'ort',
      eventlocation: 'veranstaltungsort',
      videolink: 'videolink',
    },
  },
  fr: {
    reminderMessage:
      "N'oubliez pas votre rendez-vous avec {expediteur} le {date} à {heure} à {lieuevenement}.",
    testReminderMessage: 'Rappel : {titre} le {date} à {heure}.',
    untitledEventLabel: 'votre rendez-vous',
    legacyReminderMessage: 'Rappel : {titre} le {date} à {heure}.',
    confirmationMessage: 'Confirmé : {titre} le {date} à {heure}.',
    followUpMessage: 'Merci de la part de {expediteur} !',
    confirmationLinkSuffix: 'Confirmer ou annuler : {url}',
    templateName: 'Mon Premier Modèle',
    senderName: 'Mon Entreprise',
    location: '123 Rue Principale',
    calendarStatusLabels: {
      pending: 'En attente',
      confirmed: 'Confirmé',
      cancelled: 'Annulé',
      lateCancelled: 'Annulé tardivement',
    },
    tokenAliases: {
      title: 'titre',
      date: 'date',
      time: 'heure',
      from: 'expediteur',
      location: 'lieu',
      eventlocation: 'lieuevenement',
      videolink: 'lienvideo',
    },
  },
  it: {
    reminderMessage:
      'Non dimenticare il tuo appuntamento con {mittente} il {data} alle {ora} presso {luogoevento}.',
    testReminderMessage: 'Promemoria: {titolo} il {data} alle {ora}.',
    untitledEventLabel: 'il tuo appuntamento',
    legacyReminderMessage: 'Promemoria: {titolo} il {data} alle {ora}.',
    confirmationMessage: 'Confermato: {titolo} il {data} alle {ora}.',
    followUpMessage: 'Grazie da {mittente}!',
    confirmationLinkSuffix: 'Conferma o annulla: {url}',
    templateName: 'Il Mio Primo Modello',
    senderName: 'La Mia Attività',
    location: 'Via Principale 123',
    calendarStatusLabels: {
      pending: 'In attesa',
      confirmed: 'Confermato',
      cancelled: 'Annullato',
      lateCancelled: 'Annullato in ritardo',
    },
    tokenAliases: {
      title: 'titolo',
      date: 'data',
      time: 'ora',
      from: 'mittente',
      location: 'luogo',
      eventlocation: 'luogoevento',
      videolink: 'linkvideo',
    },
  },
};

export function getLocalizedDefaults(
  language: SupportedLanguage,
): LocalizedDefaultStrings {
  return LOCALIZED_DEFAULTS[language];
}
